// src/components/Hero.js
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import Button from './Button';

// Define hero images
const heroImages = [
  '/images/hero1.jpg',
  '/images/hero2.jpg',
  '/images/hero3.jpg',
  '/images/hero4.jpg'
];

// Single hero image option
const singleHeroImage = '/images/hero1.jpg';

// Fixed HeroSection component with corrected padding
const HeroSection = styled.section`
  height: 100vh;
  position: relative;
  display: flex;
  align-items: center;
  overflow: hidden;
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    align-items: flex-start; /* Align to top on mobile */
    padding-top: 4rem; /* Space after navbar - removed duplicate */
  }
`;

const ImageSlider = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 0;
`;

// Updated SlideImage to support crossfade effect
const SlideImage = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: ${props => `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url(${props.image})`};
  background-size: cover;
  background-position: center;
  opacity: ${props => props.active ? 1 : 0};
  transition: opacity 1.5s ease-in-out;
  z-index: ${props => props.active ? 1 : 0};
`;

const SingleImage = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: ${props => `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url(${props.image})`};
  background-size: cover;
  background-position: center;
  z-index: 0;
`;

const HeroContent = styled.div`
  max-width: 1200px;
  width: 90%;
  margin: 0 auto;
  padding: 0 2rem;
  color: white;
  z-index: 1;
  position: relative;
  text-align: center;
  /* Adjusted to push content down more */
  padding-top: 3rem;
  margin-top: 0;
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    padding-top: 2rem;
    margin-top: 0;
  }
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding-top: 1rem;
    margin-top: 2rem; /* Push down more on mobile */
    padding-left: 1rem;
    padding-right: 1rem;
  }
`;

const HeroTitle = styled(motion.h1)`
  font-size: 4rem;
  line-height: 1.2;
  margin-bottom: 1.5rem;
  font-weight: 700;
  /* Added top margin to push down from navbar */
  margin-top: 3rem;
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    font-size: 3.2rem;
    margin-top: 2rem;
  }
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 2.2rem; /* Smaller font size on mobile */
    margin-top: 1rem;
  }
`;

const HeroSubtitle = styled(motion.p)`
  font-size: 1.8rem;
  margin-bottom: 3rem;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.6;
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    font-size: 1.5rem;
  }
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 1.2rem;
    margin-bottom: 2rem;
  }
`;

const ButtonGroup = styled(motion.div)`
  display: flex;
  gap: 1.5rem;
  justify-content: center;
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    flex-direction: column;
    align-items: center;
    gap: 1rem;
  }
`;

const StyledButton = styled(Button)`
  min-width: 220px;
  padding: 1rem 2rem;
  font-size: 1.1rem;
  font-weight: 600;
  text-align: center;
  border-radius: 5px;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
  }
`;

// Removed the white border from OutlineButton
const OutlineButton = styled(StyledButton)`
  background-color: transparent;
  border: none;
  color: white;
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.2);
    color: white;
  }
`;

// Moved slider controls down and made them smaller
const SlideControls = styled.div`
  position: absolute;
  bottom: 1.5rem;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 0.8rem;
  z-index: 2;
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    bottom: 4.5rem; /* Move up to avoid overlapping with buttons */
  }
`;

// Made indicators smaller
const SlideIndicator = styled.button`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: ${props => props.$active ? 'white' : 'rgba(255, 255, 255, 0.5)'};
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: white;
  }
`;

const SliderToggle = styled.div`
  position: absolute;
  bottom: 20px;
  left: 20px;
  z-index: 10;
  display: flex;
  align-items: center;
  color: white;
  background-color: rgba(0, 0, 0, 0.5);
  padding: 8px 15px;
  border-radius: 20px;
  font-size: 0.9rem;
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    bottom: 80px; /* Move up on mobile to avoid overlapping */
  }
`;

const ToggleSwitch = styled.label`
  position: relative;
  display: inline-block;
  width: 60px;
  height: 30px;
  margin-left: 10px;
  
  input {
    opacity: 0;
    width: 0;
    height: 0;
  }
  
  span {
    position: absolute;
    cursor: pointer;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(255, 255, 255, 0.3);
    transition: .4s;
    border-radius: 34px;
  }
  
  span:before {
    position: absolute;
    content: "";
    height: 22px;
    width: 22px;
    left: 4px;
    bottom: 4px;
    background-color: white;
    transition: .4s;
    border-radius: 50%;
  }
  
  input:checked + span {
    background-color: ${props => props.theme.colors.secondary};
  }
  
  input:checked + span:before {
    transform: translateX(30px);
  }
`;

const Hero = () => {
  const [currentImage, setCurrentImage] = useState(0);
  const [useSlider, setUseSlider] = useState(true);
  
  // Auto-slide every 5 seconds when slider is active
  useEffect(() => {
    if (!useSlider) return;
    
    const interval = setInterval(() => {
      setCurrentImage(prev => (prev + 1) % heroImages.length);
    }, 5000);
    
    return () => clearInterval(interval);
  }, [useSlider]);
  
  const handleIndicatorClick = (index) => {
    setCurrentImage(index);
  };
  
  const toggleSlider = () => {
    setUseSlider(prev => !prev);
  };
  
  return (
    <HeroSection>
      {useSlider ? (
        <ImageSlider>
          {heroImages.map((image, index) => (
            <SlideImage
              key={index}
              image={image}
              active={index === currentImage}
            />
          ))}
        </ImageSlider>
      ) : (
        <SingleImage image={singleHeroImage} />
      )}
      
      <HeroContent>
        <HeroTitle
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          Your Loved One's Comfort,<br />Our Sacred Duty
        </HeroTitle>
        
        <HeroSubtitle
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.3 }}
        >
          Professional and compassionate domiciliary care services tailored to meet individual needs
        </HeroSubtitle>
        
        <ButtonGroup
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.6 }}
        >
          <StyledButton to="/contact" color="secondary">
            Book a Free Care Assessment
          </StyledButton>
          <OutlineButton to="/services">
            Explore Our Services
          </OutlineButton>
        </ButtonGroup>
      </HeroContent>
      
      {useSlider && (
        <SlideControls>
          {heroImages.map((_, index) => (
            <SlideIndicator 
              key={index}
              $active={index === currentImage}
              onClick={() => handleIndicatorClick(index)}
            />
          ))}
        </SlideControls>
      )}
      
      <SliderToggle>
        <span>Slider</span>
        <ToggleSwitch>
          <input 
            type="checkbox" 
            checked={useSlider}
            onChange={toggleSlider}
          />
          <span></span>
        </ToggleSwitch>
      </SliderToggle>
    </HeroSection>
  );
};

export default Hero;